import _ from 'lodash'
import Vue from 'vue'
import Axios from 'axios'
import striptags from 'striptags'
import HtmlEntities from 'html-entities'
import { createPatch } from 'rfc6902'
import { ActionContext } from 'vuex'
import RootState from '../../state'
import ComponentState, { ComponentStore, Direction, GerberaContent, GerberaHeader, IComponentData, ImageData, Patch } from './state'
import { Getters, Mutations } from './helpers'
import ComponentBuilder from './component-builder'
import ContentEndpoint, { ContentDataSet, ContentItem } from '../../../api/gerbera/contents'
import ArticleParser from './article-parser'
import { ImageContent } from '../../../api/gerbera/core'
import attachmentUrl from '../../../filters/attachment-url'
import fileToUrl from '../../../misc/file-to-url'
import ImageCompressor from './image-compressor'
import ComponentBackupStore from './backup-store'
import escapeHtmlEntity from '../../../misc/html-entity'

type ComponentContext = ActionContext<ComponentState, RootState>

// component-style.scss を CSS に変換したテキストを定数に格納する。
//
// 変換までの過程は以下の通り。
//   1. SCSS ファイルを CSS ファイルに変換する（sass-loader）
//   2. 作られたSCSSファイルを minify し、さらに JavaScript で扱えるようにする（css-loader?minimize）
//   3. 2のうち、生成された生のCSSファイルを取得する（toString()）
const CSS_CONTENT = require('!css-loader?minimize!sass-loader!../../../assets/style/component-style.scss').toString()

export async function update(context: ComponentContext, component: IComponentData): Promise<void> {
  return recordHistory(context, () => {
    Mutations.setComponent(context, [component])
    return Promise.resolve()
  })
}

export async function remove(context: ComponentContext, component: IComponentData): Promise<void> {
  return recordHistory(context, () => {
    const builder = new ComponentBuilder(context)
    Mutations.batchUpdate(context, builder.remove(component))
    return Promise.resolve()
  })
}

interface MoveRequest {
  component: IComponentData,
  destination: IComponentData,
  direction: Direction
}

export async function move(context: ComponentContext, request: MoveRequest): Promise<void> {
  return recordHistory(context, () => {
    const builder = new ComponentBuilder(context)
    Mutations.batchUpdate(context, builder.move(
      request.component,
      {
        destination: request.destination,
        direction: request.direction
      }))
    return Promise.resolve()
  })
}

export async function addToArticle(context: ComponentContext, component: IComponentData | IComponentData[]): Promise<void> {
  const components = _.isArray(component) ? component : [component]
  return recordHistory(context, () => {
    components.forEach((c) => {
      const builder = new ComponentBuilder(context)
      Mutations.batchUpdate(context, builder.addToArticle(c))
    })
    return Promise.resolve()
  })
}

export async function updateGerberaContent(context: ComponentContext, article: GerberaContent): Promise<void> {
  return recordHistory(context, () => {
    Mutations.setGerberaContent(context, article)
    return Promise.resolve()
  })
}

export async function updateHeaderSectionContent(context: ComponentContext, content: GerberaHeader): Promise<void> {
  return recordHistory(context, () => {
    Mutations.setHeaderSectionContent(context, content)
    return Promise.resolve()
  })
}

interface HistorySource {
  gerbera: {
    title: string
  }
  data: ComponentStore
}

async function recordHistory<T>(context: ComponentContext, process: () => Promise<T>): Promise<T> {
  const before = createHistorySource(context)
  return process().then((value) => {
    const after = createHistorySource(context)
    const patch: Patch = {
      undoPatch: createPatch(after, before),
      redoPatch: createPatch(before, after)
    }
    if (patch.undoPatch.length > 0 || patch.redoPatch.length > 0) {
      Mutations.pushHistory(context, patch)
    }
    return value
  })
}

function createHistorySource(context: ComponentContext): HistorySource {
  return {
    gerbera: {
      title: context.state.gerbera.title || ''
    },
    data: _.cloneDeep(context.state.data)
  }
}

export interface ImageDataSource {
  fileName: string
  dataUrl: string
}

export async function uploadImages(context: ComponentContext, files: FileList | File[]): Promise<ImageDataSource[]> {
  const compressor = new ImageCompressor(getImageCompressorCanvas())
  return await Promise.all(_.map(files, async file => <ImageDataSource>{
    fileName: file.name,
    dataUrl: await compressor.compress(file)
  }))
}

function getImageCompressorCanvas(): HTMLCanvasElement {
  const canvas = document.getElementById('image-compressor')
  if (canvas && canvas.tagName.toLowerCase() === 'canvas') {
    return <HTMLCanvasElement>canvas
  }
  throw new Error('画像を圧縮するために、 ID が image-compressor である canvas が必要です')
}

export async function createImageData(context: ComponentContext, source: ImageDataSource): Promise<ImageData> {
  const id = Getters.idGenerator(context)()
  const fileName = createUniqueFileName(context, source)
  const image = new ImageData(id, fileName)
  image._dataUrl = source.dataUrl
  return image
}

function createUniqueFileName(context: ComponentContext, source: ImageDataSource): string {
  const existingImages = Getters.getImages(context)
  const existing = existingImages.find(img => img._dataUrl === source.dataUrl)
  if (existing) {
    // 登録済みの画像に同一データのものがある場合、既存のファイル名を採用する
    return existing.fileName
  }

  // ファイル名が重複した場合、リネームする。リネーム後の名前は以下の通り。
  // file.png => file_1.png, file_2.png ...
  // file_1.png =. file_2.png, file_3.png ...
  const pattern = /(?:_(\d+))?(\.[^.]+)?$/
  let currentName = source.fileName
  while (true) {
    if (existingImages.every(img => img.fileName !== currentName)) {
      // 名前がユニークである場合、それを登録する
      return currentName
    }
    const fixedName = currentName.replace(pattern, '')
    const match = pattern.exec(currentName)
    const index: number = (match && match[1]) ? parseInt(match[1], 10) : 0
    const extension = (match && match[2]) ? match[2] : ''
    currentName = `${fixedName}_${index + 1}${extension}`
  }
}

export interface LoadContentRequest {
  siteId: number
  contentId: number
}

/** 記事を読み込む */
export async function loadContent(context: ComponentContext, request: LoadContentRequest): Promise<void> {
  return new ContentEndpoint().getContent({
    siteId: request.siteId,
    contentId: request.contentId
  }).then((gerberaContent) => {
    if (!gerberaContent || !gerberaContent.articleJson) {
      throw new TypeError('記事の内容が空です。')
    }
    const components = new ArticleParser().nestedToFlat(gerberaContent.articleJson, Getters.idGenerator(context))
    Mutations.setAllComponents(context, components)
    Mutations.setGerberaContent(context, gerberaContent)
    Mutations.clearHistory(context)
    Mutations.markContentSaved(context)

  }).then(() => {
    // 記事の構造を読み込んだら、次は画像データを読み込む
    return Promise.all(Getters.getImages(context).map(image => loadImage(context, request, image)))
  }).then(() => {
    Mutations.clearHistory(context)
    Mutations.markContentSaved(context)
  })
}

async function loadImage(context: ComponentContext, request: LoadContentRequest, image: ImageData): Promise<void> {
  const url = attachmentUrl(image.fileName, request.siteId, request.contentId)
  return Axios.get(url, {
    withCredentials: true,
    responseType: 'blob'
  }).then((response) => {
    return fileToUrl(response.data)
  }).then((dataUrl) => {
    Mutations.setComponent(context, Object.assign({}, image, <ImageData>{
      _dataUrl: dataUrl
    }))
    Mutations.markContentSaved(context)
  })
}

export async function saveToBackup(context: ComponentContext): Promise<void> {
  await new ComponentBackupStore().put(context.state)
}

export async function loadFromBackup(context: ComponentContext, siteId: number): Promise<void> {
  if ((await getBackedUpId(context)) !== siteId) {
    throw new Error(`バックアップが存在しません: siteId=${siteId}`)
  }
  // バックアップデータの ID は現在使用中の ID と重複する可能性があるので、 ID を発番し直す
  const parser = new ArticleParser()
  const backup = await new ComponentBackupStore().getIfUnsaved()
  const data = parser.refreshComponentId(backup.data, Getters.idGenerator(context))

  // 記事が保存済みの場合、最新の公開状態をサーバから取得する
  const gerbera = await mergeServerStatus(backup.gerbera)

  Mutations.applyBackup(context, Object.assign({}, backup, <ComponentState>{
    data,
    gerbera
  }))
  Mutations.markContentChanged(context)
}

async function mergeServerStatus(content: ContentItem): Promise<ContentItem> {
  const merged = _.cloneDeep(content)

  if (!merged.contentId) {
    // 新規作成された記事の場合、サーバに保存済みの情報がないため、そのまま返す
    return merged
  }

  try {
    const contentFromServer = await new ContentEndpoint().getContent({
      siteId: merged.siteId,
      contentId: merged.contentId
    })

    merged.status = contentFromServer.status
    merged.createdDate = contentFromServer.createdDate
    merged.modifiedDate = contentFromServer.modifiedDate
    merged.publishedDate = contentFromServer.publishedDate
    merged.expiresDate = contentFromServer.expiresDate

  } catch (error) {
    // 削除済みの記事の場合、新規作成とみなす
    delete merged.contentId
    delete merged.status
    delete merged.createdDate
    delete merged.modifiedDate
    delete merged.publishedDate
    delete merged.expiresDate
  }

  return merged
}

export function removeBackup(context: ComponentContext): Promise<void> {
  return new ComponentBackupStore().remove()
}

export async function getBackedUpId(context: ComponentContext): Promise<number | null> {
  return new ComponentBackupStore().storedSiteId()
}

export async function requestSaveState(context: ComponentContext): Promise<void> {
  // 中途半端な状態で値が保存されていないコンポーネントがあるかも知れないので、保存を促す。

  // Getters.timeStampForSaveState の値を更新する
  Mutations.requestSaveState(context)

  // Getters.timeStampForSaveState の値を監視しているコンポーネントが変更を検知し、処理を実行するまで待つ。
  return Vue.nextTick()
}

export interface SaveContentRequest {
  html: string
}

/** 記事を保存する */
export async function saveContent(context: ComponentContext, request: SaveContentRequest): Promise<void> {
  const content = buildContentDataSet(context, request)
  return new ContentEndpoint().saveContent(content)
    .then((savedContent) => {
      Mutations.setGerberaContent(context, Object.assign({}, Getters.gerberaContent(context), <GerberaContent>{
        contentId: savedContent.contentId,
        status: savedContent.status
      }))
      Mutations.markContentSaved(context)
    }).then(() => {
      // バックアップの更新が失敗しても通知する必要はないので、 Promise を return しない
      removeBackup(context)
    })
}

function buildContentDataSet(context: ComponentContext, request: SaveContentRequest): ContentDataSet {
  const content = Getters.gerberaContent(context)
  const texts = Getters.getTexts(context)
  const description = htmlToDescription((texts[0] ? texts[0].text : undefined) || '')
  const images = Getters.getImages(context)
  const mainImageName = images[0] ? images[0].fileName : undefined

  // 画像ファイルを追加する時点で、「ファイル名が同一だがバイナリが異なる」画像が存在しないようにしている。
  // （function createImageData() を参照）
  // ここではファイル名の重複を排除するだけで充分。
  const uniqueImages = _.uniqBy(images, i => i.fileName)

  // 特定の文字列をHTMLエンティティへエンコードする
  const escapeText = escapeHtmlEntity(description)

  const normalizedContent: GerberaContent = Object.assign({}, content, <GerberaContent>{
    image: mainImageName,
    description: escapeText,
    articleJson: new ArticleParser().flatToNested(context.state.data),
    ogp: {
      title: content.title,
      description: escapeText,
      image: mainImageName
    }
  })
  return {
    content: normalizedContent,
    images: uniqueImages.map((i) => {
      return <ImageContent>{
        fileName: i.fileName,
        dataUrl: i._dataUrl
      }
    }),
    ewml: request.html,
    css: CSS_CONTENT
  }
}

function htmlToDescription(html: string): string {
  const stripped = striptags(html)
  const decoded = new HtmlEntities.AllHtmlEntities().decode(stripped)
  const compressed = decoded.replace(/[\s　]+/g, ' ')
  return compressed.trim()
}

export async function undo(context: ComponentContext): Promise<void> {
  Mutations.undo(context)
}

export async function redo(context: ComponentContext): Promise<void> {
  Mutations.redo(context)
}

export default {
  addToArticle,
  update,
  move,
  remove,
  updateGerberaContent,
  updateHeaderSectionContent,
  uploadImages,
  createImageData,
  loadContent,
  requestSaveState,
  saveContent,
  undo,
  redo,
  saveToBackup,
  loadFromBackup,
  removeBackup,
  getBackedUpId
}
